import React, { useEffect, useState } from 'react'
import { Modal, Steps, Button, Input, Spin, message } from 'antd'
import { showSuccessToast } from 'src/utils/messageToast'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import SupplierLogo from 'src/components/shared/SupplierLogo'
import { setCatalogueFilter } from 'src/features/FilterSlice'
import {
  quickAddSupplier,
  requestNewSupplier,
} from 'src/features/SupplierSlice'
import { RECENT_SUPPLIER_KEY } from 'src/utils/catalogueSupplierUtils'
import WizardSearchStep from './WizardSearchStep'
import {
  CONNECTING_ANIMATION_MS,
  WIZARD_PROGRESS_INDEX,
  WIZARD_STEP,
  WIZARD_STEP_TITLE,
} from './wizardSteps'

const { Step } = Steps

const ConnectSupplierWizard = ({
  visible,
  onClose,
  venueId,
  venueSuppliers = [],
  preConnectSupplierCount = 0,
  onFlowComplete,
  onAddMore,
  onInviteSupplier,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const venueCode = useSelector(state => state.venue?.venue?.code)

  const [step, setStep] = useState(WIZARD_STEP.SEARCH)
  const [supplier, setSupplier] = useState(null)
  const [accountNumber, setAccountNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const emphasizeAddMore = preConnectSupplierCount === 0
  const closable =
    step !== WIZARD_STEP.CONNECTING && step !== WIZARD_STEP.SUCCESS

  const resetWizard = () => {
    setStep(WIZARD_STEP.SEARCH)
    setSupplier(null)
    setAccountNumber('')
    setIsSubmitting(false)
  }

  useEffect(() => {
    if (visible) {
      resetWizard()
    }
  }, [visible])

  useEffect(() => {
    if (step !== WIZARD_STEP.CONNECTING || !supplier) return undefined

    const timer = setTimeout(() => {
      setStep(WIZARD_STEP.SUCCESS)
      onFlowComplete?.()
    }, CONNECTING_ANIMATION_MS)

    return () => clearTimeout(timer)
  }, [step, supplier, onFlowComplete])

  const handleClose = () => {
    resetWizard()
    onClose?.()
  }

  const handleSelectSupplier = selected => {
    setSupplier(selected)
    setStep(WIZARD_STEP.ACCOUNT_QUESTION)
  }

  const handleAccountYesSubmit = async () => {
    if (!accountNumber.trim()) {
      message.error('Please enter an account number')
      return
    }

    if (!supplier?.emailAddress || !venueId) {
      message.error('Missing required information')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await dispatch(
        quickAddSupplier(
          {
            code: venueCode,
            venueId,
            supplierEmail: supplier.emailAddress,
            supplierName: supplier.title,
            accountNumber: accountNumber.trim(),
          },
          { skipSuccessModal: true },
        ),
      )

      if (response?.success) {
        setStep(WIZARD_STEP.CONNECTING)
      } else {
        message.error(response?.message || 'Unable to add supplier')
      }
    } catch (error) {
      console.error(error)
      message.error(
        'Something went wrong while adding supplier. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleManualSetup = async () => {
    if (!supplier || !venueId) {
      message.error('Missing required information')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await dispatch(
        requestNewSupplier(
          venueId,
          supplier.title,
          supplier.emailAddress || 'info@ordersmadesimple.com',
          'TBC',
          'Account number not provided',
          false,
          { manualSetup: true },
        ),
      )

      if (response?.success) {
        showSuccessToast(
          'We will contact this supplier and email you once ordering is ready.',
        )
        onFlowComplete?.()
        handleClose()
      } else {
        message.error(response?.message || 'Unable to request supplier setup')
      }
    } catch (error) {
      console.error(error)
      message.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartOrdering = () => {
    if (venueId && supplier?.title) {
      try {
        sessionStorage.setItem(RECENT_SUPPLIER_KEY(venueId), supplier.title)
      } catch {
        // ignore storage errors
      }
      dispatch(setCatalogueFilter(supplier.title))
      history.push(`/v/${venueId}/catalogue`)
    } else if (venueId) {
      history.push(`/v/${venueId}/catalogue`)
    }
    handleClose()
  }

  const handleAddAnother = () => {
    if (onAddMore) {
      onAddMore()
    } else {
      resetWizard()
    }
  }

  const renderSupplierHeader = () => {
    if (!supplier) return null
    return (
      <SupplierHeader>
        <SupplierLogo
          logoUrl={supplier.logoUrl}
          title={supplier.title}
          size={80}
          style={{ marginRight: 0 }}
        />
        <SupplierTitle>{supplier.title}</SupplierTitle>
      </SupplierHeader>
    )
  }

  const renderStepBody = () => {
    switch (step) {
      case WIZARD_STEP.SEARCH:
        return (
          <WizardSearchStep
            venueSuppliers={venueSuppliers}
            onSelectSupplier={handleSelectSupplier}
            onInviteSupplier={supplierName => {
              handleClose()
              onInviteSupplier?.(supplierName)
            }}
          />
        )

      case WIZARD_STEP.ACCOUNT_QUESTION:
        return (
          <CenteredStep>
            {renderSupplierHeader()}
            <BodyText>
              To add this supplier, enter your account number with them.
            </BodyText>
            <Question>
              Do you know your account number with this supplier?
            </Question>
            <ButtonRow>
              <Button
                size='large'
                style={{ flex: 1 }}
                onClick={() => setStep(WIZARD_STEP.ACCOUNT_NO)}
              >
                No
              </Button>
              <Button
                type='primary'
                size='large'
                style={{ flex: 1 }}
                onClick={() => setStep(WIZARD_STEP.ACCOUNT_YES)}
              >
                Yes
              </Button>
            </ButtonRow>
            <BackLink type='link' onClick={() => setStep(WIZARD_STEP.SEARCH)}>
              Back to search
            </BackLink>
          </CenteredStep>
        )

      case WIZARD_STEP.ACCOUNT_YES:
        return (
          <CenteredStep>
            {renderSupplierHeader()}
            <BodyText>
              You&apos;re adding a supplier already on OMS. Your account number
              lets them recognise your venue and apply your agreed pricing. Some
              suppliers sync instantly, while others update overnight.
            </BodyText>
            <FieldLabel>Account Number</FieldLabel>
            <Input
              value={accountNumber}
              onChange={e => setAccountNumber(e.target.value)}
              placeholder='Enter your account number'
              size='large'
              onPressEnter={handleAccountYesSubmit}
            />
            <ButtonRow>
              <Button
                size='large'
                onClick={() => setStep(WIZARD_STEP.ACCOUNT_QUESTION)}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type='primary'
                size='large'
                onClick={handleAccountYesSubmit}
                loading={isSubmitting}
              >
                Confirm
              </Button>
            </ButtonRow>
          </CenteredStep>
        )

      case WIZARD_STEP.ACCOUNT_NO:
        return (
          <CenteredStep>
            {renderSupplierHeader()}
            <BodyText>
              No account number? Tap below and we&apos;ll contact the supplier
              for you. We&apos;ll email you as soon as they&apos;re ready so you
              can start ordering.
            </BodyText>
            <HelperText>
              This might take up to a couple of days, but you&apos;ll always pay
              your agreed prices.
            </HelperText>
            <ButtonRow>
              <Button
                size='large'
                onClick={() => setStep(WIZARD_STEP.ACCOUNT_QUESTION)}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type='primary'
                size='large'
                onClick={handleManualSetup}
                loading={isSubmitting}
              >
                Ask OMS to contact them
              </Button>
            </ButtonRow>
          </CenteredStep>
        )

      case WIZARD_STEP.CONNECTING:
        return (
          <CenteredStep>
            {renderSupplierHeader()}
            <Spin size='large' />
            <BodyText style={{ marginTop: 20 }}>
              Adding {supplier?.title || 'supplier'}...
            </BodyText>
          </CenteredStep>
        )

      case WIZARD_STEP.SUCCESS:
        return (
          <CenteredStep>
            {renderSupplierHeader()}
            {emphasizeAddMore ? (
              <SuccessMessage>
                Who else do you order from? Add your other suppliers — it&apos;s
                free.
              </SuccessMessage>
            ) : (
              <>
                <SuccessMessage>
                  You can start ordering right away. Pricing will populate as
                  soon as the supplier shares their latest catalogue (usually
                  overnight).
                </SuccessMessage>
                <HelperText>
                  Go to the Catalogue tab to browse products from this supplier
                  and add them to your favourites.
                </HelperText>
              </>
            )}
            <ButtonRow>
              {emphasizeAddMore ? (
                <>
                  <Button
                    type='primary'
                    size='large'
                    onClick={handleAddAnother}
                  >
                    Add another supplier
                  </Button>
                  <Button size='large' onClick={handleStartOrdering}>
                    Go to Catalogue
                  </Button>
                </>
              ) : (
                <>
                  <Button size='large' onClick={handleAddAnother}>
                    Add another supplier
                  </Button>
                  <Button
                    type='primary'
                    size='large'
                    onClick={handleStartOrdering}
                  >
                    Go to Catalogue
                  </Button>
                </>
              )}
            </ButtonRow>
          </CenteredStep>
        )

      default:
        return null
    }
  }

  const showProgress =
    step !== WIZARD_STEP.CONNECTING && step !== WIZARD_STEP.SUCCESS

  return (
    <Modal
      title={WIZARD_STEP_TITLE[step]}
      visible={visible}
      onCancel={closable ? handleClose : undefined}
      footer={null}
      width={step === WIZARD_STEP.SEARCH ? 600 : 500}
      centered
      closable={closable}
      maskClosable={closable}
      keyboard={closable}
      destroyOnClose
    >
      {showProgress && (
        <Steps
          current={WIZARD_PROGRESS_INDEX[step]}
          size='small'
          style={{ marginBottom: 24 }}
        >
          <Step title='Search' />
          <Step title='Account' />
          <Step title='Connect' />
          <Step title='Done' />
        </Steps>
      )}
      {renderStepBody()}
    </Modal>
  )
}

const CenteredStep = styled.div`
  padding: 8px 4px 4px;
  text-align: center;
`

const SupplierHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`

const SupplierTitle = styled.h3`
  margin-top: 12px;
  font-size: 18px;
  font-weight: 600;
`

const BodyText = styled.p`
  font-size: 14px;
  color: #595959;
  line-height: 1.6;
  margin-bottom: 16px;
`

const Question = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 20px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.6;
  margin-bottom: 20px;
`

const SuccessMessage = styled.p`
  font-size: 16px;
  color: #262626;
  line-height: 1.6;
  margin-bottom: 12px;
  font-weight: 500;
`

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #262626;
  text-align: left;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
`

const BackLink = styled(Button)`
  margin-top: 12px;
  padding: 0;
`

export default ConnectSupplierWizard
